import { useState, useEffect, useRef, useMemo } from "react"
import axios from 'axios'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useAuth } from '../hooks/Auth'
import Loading from './Loading'
import { convertTimestampToTime, convertTimestampToDate } from './../utils'

const columnHelper = createColumnHelper()

const tableStyle = {
  height: "32.35em",
  display: "block",
  overflowY: "auto"
}

const cellStyle = {
  paddingTop: 1,
  paddingBottom: 1,
  maxWidth: 600,
  overflow: "hidden"
}

function EventTable({ facility, search }) {
  const auth = useAuth()
  const [index, setIndex] = useState(0)
  const [limit, setLimit] = useState(20)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [status, setStatus] = useState("fetched")

  let columns = useMemo(() => [
    columnHelper.accessor('version', {
      header: () => 'Version',
      cell: info => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor('timestamp', {
      header: () => 'Time',
      cell: info => <span>{convertTimestampToDate(info.getValue())} {convertTimestampToTime(info.getValue())}</span>,
    }),
    columnHelper.accessor('type', {
      header: () => 'Type',
      cell: info => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor('data', {
      header: () => 'Data',
      cell: info => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor('meta', {
      id: 'user',
      header: () => 'User',
      cell: info => <span>{JSON.parse(info.getValue()).user_id||"-"}</span>,
    }),
    columnHelper.accessor('meta', {
      id: 'corr',
      header: () => 'Correlation id',
      cell: info => <span>{JSON.parse(info.getValue()).correlation_id||"-"}</span>,
    })
  ], [])

  useEffect(() => {
    const fetchEvents = async () => {
      const params = { id: facility.id, limit: 1, desc: true }
      try {
        const rsp = await axios.get("/api/v1/facility/events", { params })
        if (rsp.data.length) {
          let c = rsp.data[0].version + 1
          setCount(c)
          setPageCount(Math.ceil(c / limit))
        }
      }
      catch (e) {
        console.log(e)
      }
    }
    fetchEvents()
  }, [auth.token, facility.id, limit])

  useEffect(() => {
    const offset = count - (index * limit)
    const fetchEvents = async () => {
      const params = { id: facility.id, index: offset, limit, desc: true }
      if (search) params.search = search
      setStatus("fetching")
      try {
        const rsp = await axios.get("/api/v1/facility/events", { params })
        setData(rsp.data)
        setStatus("fetched")
      }
      catch (e) {
        setStatus("error")
        console.log(e)
      }
    }
    fetchEvents()
  }, [auth.token, facility.id, index, limit, search, pageCount])

  const table = useReactTable({
    columns,
    data,
    state: {},
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="h-100">
      <div className='mt-auto container-fluid row'>
        <div className='col px-1 pt-2'>
          {status !== "fetched" && <span>Loading...</span>}
        </div>
        <div className='col px-1 pt-2'>
          <select
           className='custom-select custom-select-sm'
           value={limit}
           onChange={e => setLimit(Number(e.target.value))}
          >
            {[20, 50, 100].map(pageSize => <option key={pageSize} value={pageSize}>Show {pageSize}</option>)}
          </select>
        </div>
        <div className="col-auto py-0">
          <ul className='pagination justify-content-end'>
            <span className='pt-2 pr-1'>Page{' '}</span>
            <strong className='pt-2 pr-2'>
              {index + 1} of {pageCount}
            </strong>
            <li className='page-item' onClick={() => setIndex(0)}>
              <button className='page-link px-2'>{'<<'}</button>
            </li>
            {' '}
            <li className='page-item' onClick={() => setIndex(Math.max(index - 1, 0))}>
              <button className='page-link px-2'>{'<'}</button>
            </li>
            {' '}
            <li className='page-item' onClick={() => setIndex(Math.min(index + 1, pageCount - 1))}>
              <button className='page-link px-2'>{'>'}</button>
            </li>
            {' '}
            <li className='page-item' onClick={() => setIndex(pageCount - 1)}>
              <button className='page-link px-2'>{'>>'}</button>
            </li>
          </ul>
        </div>
      </div>
      {status !== "fetched" ?
      <Loading/> :
      <table className="table table-hover table-flush align-items-center">
        <thead className="thead-light">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id} style={cellStyle}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>}
    </div>
  )
}

function LogsTable({ facility }) {
  const auth = useAuth()
  const [records, setRecords] = useState([])
  const [lastLog, setLastLog] = useState(null)
  const ws = useRef(null)

  useEffect(() => {
    setRecords([])
    setLastLog(null)
    // React to events
    ws.current = new EventSource(`/api/v1/logging/${facility.name}?tail=true&token=${auth.token}`)
    ws.current.onmessage = (e) => {
      if (e.data.includes('ping')) return
      let line = e.data.split(' ')
      setLastLog({
        // Add 20 in front of year
        date: new Date('20' + line.shift() + 'T' + line.shift() + '.000Z'),
        level: line.shift(),
        name: line.shift().replace('logger.', ''),
        message: line.join(' ')
      })
    }
    ws.current.onerror = e => {
      ws.current.close()
    }
    return () => { ws.current.close() }
  }, [facility.name, auth.token])

  useEffect(() => {
    if (lastLog && (!records.length || records[records.length - 1] !== lastLog))
      setRecords([...records, lastLog])
  }, [records, lastLog])

  return (
    <table className='table table-flush table-responsive-xl align-items-center w-100'>
      <thead className='thead-light'>
        <tr role="row">
          <th colSpan="1" role="columnheader">Time</th>
          <th colSpan="1" role="columnheader">Level</th>
          <th colSpan="1" role="columnheader">Name</th>
          <th colSpan="1" role="columnheader">Message</th>
        </tr>
      </thead>
      <tbody>
      {records.map((line, i) => (
        <tr key={i}>
          <td>{line.date.toLocaleString()}</td>
          <td>{line.level}</td>
          <td>{line.name}</td>
          <td>{line.message}</td>
        </tr>
      ))}
      </tbody>
    </table>
  )
}

export default function Logs({ facility }) {
  const [search, setSearch] = useState("")
  const [toggle, setToggle] = useState(false)

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col">
          <div className='card shadow' style={{minHeight: 670}}>
            <div className='card-header bg-transparent'>
              <div className='row align-items-center'>
                <div className='col'>
                  <h6 className='text-uppercase text-muted ls-1 mb-1'>{facility.name}</h6>
                  <h2 className="mb-0">Logs</h2>
                </div>
                <div className='col-auto'>
                  <label className="custom-toggle">
                    <input type="checkbox" onChange={() => setToggle(!toggle)}/>
                    <span className="custom-toggle-slider rounded-circle" data-label-off="Log" data-label-on="Event"/>
                  </label>
                </div>
                <div className='col-5'>
                  <textarea
                    className='form-control'
                    rows={1}
                    resize='none'
                    placeholder='search'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className='d-flex flex-column h-100' style={tableStyle}>
            {toggle ?
              <EventTable facility={facility} search={search}/> :
              <LogsTable facility={facility}/>
            }
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
